import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Axios } from 'axios';
import { useApi } from '@/hooks/useApi';

export enum ContactType {
  call = 1,
  message = 2,
}

export type ContactTypes = keyof typeof ContactType;

export interface ContactView {
  created_at: string;
  entry_id: number;
  id: number;
  type: ContactType.call | ContactType.message;
  user_email: string;
  user_first_name: string;
  user_id: number;
  user_last_name: string;
}

export interface Contact {
  uuid: string;
  name: string;
  phoneNumber: string;
}
type ContactStatisticsIncrementAPIResponse =
  | { success: false }
  | {
      success: true;
      contacts: Contact[];
    };

export const fetchContactStats = (client: Axios, entryId: string) =>
  client
    .get<{ contactViews: Record<ContactTypes, ContactView[]> }>(`/api/entries/${entryId}/contact-statistics`)
    .then((response) => response.data);

export const fetchContactStat = (client: Axios, entryId: string, type: ContactTypes) =>
  client
    .get<{ contactViews: ContactView[] }>(`/api/entries/${entryId}/contact-statistics/${type}`)
    .then((response) => response.data);

export const incrementContactStats = (client: Axios, entryId: number | string, type: ContactTypes) =>
  client
    .post<ContactStatisticsIncrementAPIResponse>(`/api/entries/${entryId}/contact-statistics`, { entryId, type })
    .then((response) => response.data);

export const useContactStats = (entryId: string) => {
  const [stats, setStats] = useState<Record<ContactTypes, ContactView[]> | null>(null);
  const { apiClient } = useApi();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: (entryId: string) => fetchContactStats(apiClient, entryId),
    onSuccess: (result) => {
      setStats(result.contactViews);
    },
  });

  useEffect(() => {
    if (!stats && !isPending && !isError && !isSuccess) {
      mutate(entryId);
    }
  }, [entryId, mutate, stats, isPending, isError, isSuccess]);

  return stats;
};
